import { apiClient, throwApiError } from './apiClient';
import type { ProjectInsert, ProjectUpdate } from '@urbanmetrix/sdk';

type ProjectSpecs = {
  numberOfBuildings: number;
  buildingUse: 'Residential' | 'Office';
  grossFloorSurface: number | null;
  numberOfFloors: number | null;
};

type ProjectSimulationChild = {
  id: string | number;
  name?: string | null;
  status?: string | null;
  simType?: string | null;
};

type ProjectSimulationSetChild = {
  id: string | number;
  name?: string | null;
  description?: string | null;
  deletedAt?: string | null;
  simulations: ProjectSimulationChild[];
};

type ProjectGeodataChild = {
  id: string | number;
  name?: string | null;
  status?: string | null;
  progressStatus?: string | null;
  errorMessage?: string | null;
  failedOnStep?: string | null;
  execTime?: number | null;
  deletedAt?: string | null;
  hasTerrain?: boolean | null;
  hasBuilding?: boolean | null;
  hasVegetation?: boolean | null;
  hasTargets?: boolean | null;
  simulationSets: ProjectSimulationSetChild[];
};

type ProjectGridChild = {
  id: string | number;
  name?: string | null;
  description?: string | null;
  status?: string | null;
  progressStatus?: string | null;
  deletedAt?: string | null;
  geodatas: ProjectGeodataChild[];
};

export type ProjectWithChildren = Awaited<ReturnType<typeof getProject>> & {
  grids: ProjectGridChild[];
};

export async function getProjects(includeSizes = false) {
  const response = await (
    apiClient.api.projects.get as unknown as (options: {
      query: { sizes: 'true' | 'false' };
    }) => ReturnType<typeof apiClient.api.projects.get>
  )({ query: { sizes: String(includeSizes) as 'true' | 'false' } });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProject(id: string) {
  const response = await apiClient.api.projects({ projectId: id }).get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export async function getProjectWithChildren(id: string) {
  const response = await apiClient.api.projects({ projectId: id }).get({
    query: { include: 'children' },
  });
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data as ProjectWithChildren;
}

export async function getProjectUsers(id: string) {
  const response = await apiClient.api.projects({ projectId: id }).users.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function addProjectUser(projectId: string, userId: string) {
  const response = await apiClient.api.projects({ projectId }).users({ userId }).post();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function removeProjectUser(projectId: string, userId: string) {
  const response = await apiClient.api.projects({ projectId }).users({ userId }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function addProject(project: ProjectInsert & ProjectSpecs) {
  const response = await apiClient.api.projects.post(project as ProjectInsert);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateProject(id: string, project: ProjectUpdate & Partial<ProjectSpecs>) {
  const response = await apiClient.api.projects({ projectId: id }).patch(project as ProjectUpdate);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function setProjectSupportFlag(id: string, supportFlagged: boolean) {
  const endpoint = apiClient.api.projects({ projectId: id }) as unknown as {
    'support-flag': {
      patch: (payload: { supportFlagged: boolean }) => Promise<{
        data: Awaited<ReturnType<typeof getProject>>;
        error: { value: unknown; status: number } | null;
      }>;
    };
  };
  const response = await endpoint['support-flag'].patch({ supportFlagged });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function deleteProject(id: string) {
  const response = await apiClient.api.projects({ projectId: id }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProjectImportedFiles(projectId: string) {
  const response = await apiClient.api.projects({ projectId })['imported-files'].get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function uploadProjectImportedFile(
  projectId: string,
  kind: 'customObstruction' | 'projectSketches',
  file: File
) {
  const response = await apiClient.api.projects({ projectId })['imported-files'].post({ kind, file });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProjectImportedFile(projectId: string, importedFileId: string) {
  const response = await apiClient.api.projects({ projectId })['imported-files']({ importedFileId }).get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateProjectImportedFile(
  projectId: string,
  importedFileId: string,
  importedFile: { name?: string }
) {
  const response = await apiClient.api
    .projects({ projectId })
    ['imported-files']({ importedFileId })
    .patch(importedFile);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function deleteProjectImportedFile(projectId: string, importedFileId: string) {
  const response = await apiClient.api.projects({ projectId })['imported-files']({ importedFileId }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProjectExportedFiles(projectId: string, gridId?: string) {
  const endpoint = apiClient.api.projects({ projectId })['exported-files'];
  const response = gridId ? await endpoint.get({ query: { grid_id: gridId } }) : await endpoint.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProjectExportedFile(projectId: string, exportedFileId: string) {
  const response = await apiClient.api.projects({ projectId })['exported-files']({ exportedFileId }).get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateProjectExportedFile(
  projectId: string,
  exportedFileId: string,
  exportedFile: { name?: string }
) {
  const response = await apiClient.api
    .projects({ projectId })
    ['exported-files']({ exportedFileId })
    .patch(exportedFile);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function deleteProjectExportedFile(projectId: string, exportedFileId: string) {
  const response = await apiClient.api.projects({ projectId })['exported-files']({ exportedFileId }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProjectReports(id: string) {
  const response = await apiClient.api.projects({ projectId: id }).reports.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateProjectReport(projectId: string, reportId: string, report: { name?: string }) {
  const response = await apiClient.api.projects({ projectId }).reports({ reportId }).patch(report);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function deleteProjectReport(projectId: string, reportId: string) {
  const response = await apiClient.api.projects({ projectId }).reports({ reportId }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProjectObservations(projectId: string, reportId: string) {
  const response = await apiClient.api.projects({ projectId }).reports({ reportId }).observations.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function reorderProjectObservations(projectId: string, reportId: string, ids: string[]) {
  const response = await apiClient.api.projects({ projectId }).reports({ reportId }).observations.order.post({ ids });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getProjectObservation(projectId: string, reportId: string, observationId: string) {
  const response = await apiClient.api
    .projects({ projectId })
    .reports({ reportId })
    .observations({ id: observationId })
    .get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateProjectObservation(
  projectId: string,
  reportId: string,
  observationId: string,
  observation: { title?: string; description?: string | null }
) {
  const response = await apiClient.api
    .projects({ projectId })
    .reports({ reportId })
    .observations({ id: observationId })
    .patch(observation);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function replaceProjectObservationImage(
  projectId: string,
  reportId: string,
  observationId: string,
  image: File
) {
  const response = await apiClient.api
    .projects({ projectId })
    .reports({ reportId })
    .observations({ id: observationId })
    .image.put({ image });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function deleteProjectObservation(projectId: string, reportId: string, observationId: string) {
  const response = await apiClient.api
    .projects({ projectId })
    .reports({ reportId })
    .observations({ id: observationId })
    .delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function copyProjectObservation(
  projectId: string,
  reportId: string,
  observationId: string,
  targetReportId: string
) {
  const response = await apiClient.api
    .projects({ projectId })
    .reports({ reportId })
    .observations({ id: observationId })
    .copy.post({ targetReportId });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}
